(() => {
  if (customElements.get('pe-product-gallery')) return;
  const requests = new Map();
  class ProductGallery extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      const grid = this.querySelector('[data-gallery-grid]');
      const status = this.querySelector('[data-gallery-status]');
      const more = this.querySelector('[data-gallery-more]');
      try {
        const source = this.dataset.source;
        if (!requests.has(source)) requests.set(source, (async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          try {
            const response = await fetch(source, { signal: controller.signal });
          if (!response.ok) throw new Error('Gallery unavailable');
            return await response.json();
          } finally { clearTimeout(timeout); }
        })().catch(error => { requests.delete(source); throw error; }));
        const data = await requests.get(source);
        if (!Array.isArray(data.images)) throw new Error('Invalid gallery');
        const urls = [...new Set(data.images)].filter(value => {
          try { return new URL(value).protocol === 'https:'; } catch { return false; }
        });
        const limit = Math.max(1, Math.min(12, Number(this.dataset.pageSize) || 8));
        const ready = [];
        let shown = limit;
        const update = () => {
          ready.forEach((tile, index) => { tile.hidden = index >= shown; });
          more.hidden = ready.length <= shown;
          status.textContent = ready.length ? `Showing ${Math.min(shown, ready.length)} of ${ready.length} images.` : '';
        };
        more.addEventListener('click', () => {
          const firstNew = shown;
          shown += limit;
          update();
          ready[firstNew]?.focus({ preventScroll: true });
        });
        // Test loading without inserting failed images into the visible grid.
        // Four requests at a time keep the gallery from flooding the connection.
        let next = 0;
        let checked = 0;
        const deadline = Date.now() + 20000;
        const tiles = new Array(urls.length);
        const loadOne = source => new Promise(resolve => {
          const image = new Image();
          const timer = setTimeout(() => resolve(null), Math.max(1, Math.min(6000, deadline - Date.now())));
          image.onload = () => { clearTimeout(timer); resolve(image); };
          image.onerror = () => { clearTimeout(timer); resolve(null); };
          image.src = source;
        });
        const worker = async () => {
          while (next < urls.length && Date.now() < deadline) {
            const index = next++;
            const image = await loadOne(urls[index]);
            checked++;
            if (!ready.length) status.textContent = `Checking gallery images (${checked} of ${urls.length})…`;
            if (!image) continue;
            const link = document.createElement('a');
            link.href = urls[index];
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.setAttribute('aria-label', `Open gallery image ${index + 1} in a new tab`);
            image.alt = 'Footwear gallery image';
            image.decoding = 'async';
            image.width = 400;
            image.height = 400;
            link.append(image);
            tiles[index] = link;
            ready.splice(0, ready.length, ...tiles.filter(Boolean));
            grid.replaceChildren(...ready);
            update();
          }
        };
        await Promise.all(Array.from({ length: Math.min(4, urls.length) }, worker));
        if (!ready.length) {
          if (this.dataset.editor === 'true') status.textContent = 'No gallery images could load. Check the original image URLs or upload the images to Shopify Files.';
          else this.hidden = true;
        }
      } catch {
        if (this.dataset.editor === 'true') status.textContent = 'Gallery unavailable. Check pe-product-gallery.json and its image URLs.';
        else this.hidden = true;
      }
    }
  }
  customElements.define('pe-product-gallery', ProductGallery);
})();
